import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import NotificationBell from "@/components/NotificationBell";
import { TIER_META } from "@/components/TierBadge";

// Colours coordinated with each 3D icon — used for the active-state glow.
const NAV_COLOR = {
  "chart": "#0A84FF",         // blue — Dashboard
  "house": "#FF9500",         // orange — Schede
  "money-bag": "#F59E0B",     // amber — Donazioni / Piano
  "pin": "#0A84FF",           // blue — Community
  "thumbs-up": "#34C759",     // green — Sostienici
  "hourglass": "#AF52DE",     // violet — Vacanti
  "gear": "#12466F",          // deep navy — Setting
};

// Airbnb-style 3D icon rendered from `/public/nav-icons/{slug}.png`. Active
// state applies only the pill's tinted background + label colour — no glow
// on the icon itself (per user request).
const NavPngIcon = ({ slug, alt }) => (
  <img
    src={`/nav-icons/${slug}.png`}
    alt={alt}
    aria-hidden="true"
    className="w-10 h-10 object-contain select-none"
    onError={(e) => { e.currentTarget.style.display = "none"; }}
  />
);

const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  if (!user) return null;

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  // `icon` may be either a slug string (→ PNG file) or a lucide component.
  // Active state: no solid blue pill anymore — the icon glows in its own tint
  // and the pill picks up a soft tinted background matching the icon.
  const NavLink = ({ to, icon, label, testId, isActive }) => {
    const isPng = typeof icon === "string";
    const color = isPng ? (NAV_COLOR[icon] || "#007AFF") : "#007AFF";
    const iconEl = isPng
      ? <NavPngIcon slug={icon} alt={label} />
      : (() => {
          const I = icon;
          return (
            <I
              className="w-7 h-7 transition-colors duration-300"
              strokeWidth={1.6}
              style={isActive ? { color } : undefined}
            />
          );
        })();
    return (
      <Link to={to} data-testid={testId}>
        <div
          className={`flex items-center gap-2 px-3.5 h-12 rounded-full text-sm transition-colors ${
            isActive ? "font-semibold" : "font-medium text-slate-700 hover:bg-slate-100"
          }`}
          style={isActive ? { backgroundColor: `${color}1F`, color } : undefined}
        >
          {iconEl}
          <span className="hidden md:inline">{label}</span>
        </div>
      </Link>
    );
  };

  const isDashboard = location.pathname === "/dashboard";
  const isMP = location.pathname.startsWith("/community");
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <header className="h-16 ios-glass sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6 border-b border-white/60">
      <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="header-logo">
        <img src="/immomap-logo.png" alt="ImmoMap" className="w-12 h-12 object-contain rounded-xl" />
        <div>
          <div className="flex items-center gap-1.5">
            <div className="font-display font-bold text-base tracking-tight leading-none" style={{ color: "#12466F" }}>ImmoMap</div>
            {(() => {
              // Plan chip mirrors the tier badge visual language: 3D emoji icon
              // (clock / star / trophy) + coloured label + subtle tinted background.
              const tier = (user?.tier || "demo").toLowerCase();
              const meta = TIER_META[tier] || TIER_META.demo;
              return (
                <span
                  data-testid={`header-plan-badge-${tier}`}
                  className="inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.18em] uppercase leading-none pl-1 pr-1.5 py-0.5 rounded-md shadow-sm"
                  style={{
                    background: `${meta.color}18`,
                    color: meta.color,
                    border: `1px solid ${meta.color}55`,
                  }}
                  title={`Piano: ${meta.label}`}
                >
                  <img
                    src={`/board-icons/${meta.icon}.png`}
                    alt=""
                    aria-hidden="true"
                    className="w-3.5 h-3.5 object-contain"
                    style={{ filter: `drop-shadow(0 1px 1.5px ${meta.color}80)` }}
                  />
                  {meta.label}
                </span>
              );
            })()}
          </div>
          <div className="text-[9.5px] tracking-widest text-slate-500 uppercase mt-1">Real Estate Mapping</div>
        </div>
      </Link>

      <nav className="flex items-center gap-1">
        <NavLink to="/dashboard" icon="chart" label="Dashboard" testId="nav-dashboard" isActive={isDashboard} />
        <NavLink to="/community" icon="group" label="Community" testId="nav-melting" isActive={isMP} />
        {user.role === "admin" && (
          <NavLink to="/admin/vacant-boards" icon="hourglass" label="Vacanti" testId="nav-admin-vacant" isActive={location.pathname === "/admin/vacant-boards"} />
        )}
        {user.role === "admin" && (
          <NavLink to="/admin/settings" icon="gear" label="Setting" testId="nav-admin-settings" isActive={location.pathname.startsWith("/admin/settings")} />
        )}
      </nav>

      <div className="flex items-center gap-3">
        <NotificationBell />
        <Link to="/account" className="flex items-center gap-2 rounded-full hover:bg-slate-100 px-2 py-1 transition-colors" data-testid="header-account-btn" title="Il mio account">
          <Avatar className="w-8 h-8 rounded-full">
            {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback className="rounded-full text-xs">{(user.name || user.email)[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="hidden sm:block leading-tight">
            <div className="text-xs font-medium">{user.name || user.email}</div>
            <Badge variant="outline" className="rounded-full text-[9px] uppercase tracking-widest px-1.5 py-0 h-4 border-slate-300 bg-white">
              {user.role}
            </Badge>
          </div>
        </Link>
        <Button variant="ghost" size="sm" onClick={doLogout} data-testid="header-logout-btn" className="rounded-full h-8 w-8 p-0" title="Logout">
          <LogOut className="w-4 h-4" />
        </Button>
      </div>
    </header>
  );
};

export default AppHeader;
